"use client";

import { LogOut, Upload } from "lucide-react";
import { useTranslations } from "next-intl";
import { Button } from "@artha/ui";
import type { CandidateEditableProfileDto } from "@artha/api-contracts";

function PreferenceList({
  label,
  values,
}: {
  label: string;
  values: string[];
}) {
  if (values.length === 0) return null;

  return (
    <div className="space-y-2">
      <p className="text-xs font-bold uppercase tracking-wide text-[#5C512F]">
        {label}
      </p>
      <div className="flex flex-wrap gap-2">
        {values.map((value) => (
          <span
            key={`${label}-${value}`}
            className="rounded-full border border-black bg-white px-3 py-1.5 text-xs font-semibold text-[#1D2939]"
          >
            {value}
          </span>
        ))}
      </div>
    </div>
  );
}

type ManageStepProps = {
  profile: CandidateEditableProfileDto | null;
  isLoggingOut: boolean;
  onUpdate: () => void;
  onLogout: () => void;
};

export function ManageStep({
  profile,
  isLoggingOut,
  onUpdate,
  onLogout,
}: ManageStepProps) {
  const t = useTranslations("profile.resumePersonalization");

  return (
    <div className="space-y-5">
      <PreferenceList
        label={t("review.titles")}
        values={profile?.titles || []}
      />
      <PreferenceList
        label={t("review.skills")}
        values={profile?.skills || []}
      />
      <PreferenceList
        label={t("review.keywords")}
        values={profile?.keywords || []}
      />
      <Button
        type="button"
        className="h-12 w-full gap-2 rounded-[10px] bg-primary text-sm font-bold text-primary-foreground"
        onClick={onUpdate}
      >
        <Upload className="h-4 w-4" />
        {t("actions.update")}
      </Button>
      <button
        type="button"
        className="flex w-full items-center justify-center gap-2 text-sm font-semibold text-[#9D1C1C]"
        disabled={isLoggingOut}
        onClick={onLogout}
      >
        <LogOut className="h-4 w-4" />
        {isLoggingOut ? t("actions.loggingOut") : t("actions.logout")}
      </button>
    </div>
  );
}
